import { API } from '@/config/api';
import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Bell, BellOff, Smartphone, Check, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';


export default function NotificationSettings() {
  const [settings, setSettings] = useState({
    dose_reminders: true,
    low_stock_alerts: true,
    expiry_warnings: true,
    achievement_unlocked: true,
    weekly_summary: false,
    marketing_emails: false
  });
  const [pushSupported, setPushSupported] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);

  useEffect(() => {
    // Check push notification support
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      setPushSupported(true);
      checkPushSubscription();
    }
  }, []);

  const checkPushSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setPushSubscribed(!!subscription);
    } catch (error) {
      console.error('Error checking push subscription:', error);
    }
  };

  const subscribeToPush = async () => {
    setPushLoading(true);
    try {
      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error('Notification permission denied');
        return;
      }

      // Get VAPID public key
      const vapidRes = await fetch(`${API}/push/vapid-key`, { credentials: 'include' });
      if (!vapidRes.ok) throw new Error('Failed to get VAPID key');
      const { publicKey } = await vapidRes.json();

      // Convert VAPID key
      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        return Uint8Array.from([...rawData].map(char => char.charCodeAt(0)));
      };

      // Subscribe to push
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });

      // Send subscription to backend
      const subscribeRes = await fetch(`${API}/push/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(subscription.toJSON())
      });

      if (subscribeRes.ok) {
        setPushSubscribed(true);
        toast.success('Push notifications enabled!');
      } else {
        throw new Error('Failed to save subscription');
      }
    } catch (error) {
      console.error('Push subscription error:', error);
      toast.error('Failed to enable push notifications');
    } finally {
      setPushLoading(false);
    }
  };

  const unsubscribeFromPush = async () => {
    setPushLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
        
        await fetch(`${API}/push/unsubscribe?endpoint=${encodeURIComponent(subscription.endpoint)}`, {
          method: 'DELETE',
          credentials: 'include'
        });
      }

      setPushSubscribed(false);
      toast.success('Push notifications disabled');
    } catch (error) {
      console.error('Unsubscribe error:', error);
      toast.error('Failed to disable push notifications');
    } finally {
      setPushLoading(false);
    }
  };

  const testPushNotification = async () => {
    try {
      const res = await fetch(`${API}/push/test`, {
        method: 'POST',
        credentials: 'include'
      });

      if (res.ok) {
        toast.success('Test notification sent!');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to send test notification');
      }
    } catch (error) {
      toast.error('Failed to send test notification');
    }
  };

  const handleToggle = (key) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
    toast.success('Notification settings updated');
  };

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto" data-testid="notification-settings-page">
      <div className="flex items-center gap-3 mb-8">
        <Bell className="h-8 w-8 text-primary" />
        <h1 className="text-3xl md:text-4xl font-bold">Notification Settings</h1>
      </div>

      {/* Push Notifications Card */}
      <Card className="p-6 glass-card mb-6">
        <div className="flex items-start gap-4 mb-6">
          <div className="p-3 rounded-lg bg-primary/10">
            <Smartphone className="h-6 w-6 text-primary" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-semibold mb-1">Push Notifications</h2>
            <p className="text-sm text-muted-foreground">
              Receive real-time notifications on your device for dose reminders, alerts, and more.
            </p>
          </div>
        </div>

        {!pushSupported ? (
          <div className="flex items-center gap-2 p-4 rounded-lg bg-warning/10 border border-warning/30">
            <AlertTriangle className="h-5 w-5 text-warning" />
            <p className="text-sm text-warning">
              Push notifications are not supported in your browser.
            </p>
          </div>
        ) : pushSubscribed ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 p-4 rounded-lg bg-success/10 border border-success/30">
              <Check className="h-5 w-5 text-success" />
              <p className="text-sm text-success">Push notifications are enabled</p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={testPushNotification}>
                Test Notification
              </Button>
              <Button variant="destructive" onClick={unsubscribeFromPush} disabled={pushLoading}>
                {pushLoading ? 'Processing...' : 'Disable Push'}
              </Button>
            </div>
          </div>
        ) : (
          <Button onClick={subscribeToPush} disabled={pushLoading} className="w-full sm:w-auto">
            {pushLoading ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full mr-2" />
                Enabling...
              </>
            ) : (
              <>
                <Bell className="h-4 w-4 mr-2" />
                Enable Push Notifications
              </>
            )}
          </Button>
        )}
      </Card>

      {/* Notification Types Card */}
      <Card className="p-6 glass-card">
        <h2 className="text-xl font-semibold mb-6">Notification Preferences</h2>
        
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Dose Reminders</p>
              <p className="text-sm text-muted-foreground">
                Get notified when it's time for your scheduled doses
              </p>
            </div>
            <Switch
              checked={settings.dose_reminders}
              onCheckedChange={() => handleToggle('dose_reminders')}
            />
          </div>

          <div className="border-t border-border pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Low Stock Alerts</p>
                <p className="text-sm text-muted-foreground">
                  Alerts when vials are running low
                </p>
              </div>
              <Switch
                checked={settings.low_stock_alerts}
                onCheckedChange={() => handleToggle('low_stock_alerts')}
              />
            </div>
          </div>

          <div className="border-t border-border pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Expiry Warnings</p>
                <p className="text-sm text-muted-foreground">
                  Get notified before vials expire
                </p>
              </div>
              <Switch
                checked={settings.expiry_warnings}
                onCheckedChange={() => handleToggle('expiry_warnings')}
              />
            </div>
          </div>

          <div className="border-t border-border pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Achievement Unlocked</p>
                <p className="text-sm text-muted-foreground">
                  Celebrate when you unlock new achievements
                </p>
              </div>
              <Switch
                checked={settings.achievement_unlocked}
                onCheckedChange={() => handleToggle('achievement_unlocked')}
              />
            </div>
          </div>

          <div className="border-t border-border pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Weekly Summary</p>
                <p className="text-sm text-muted-foreground">
                  Receive weekly research summaries via email
                </p>
              </div>
              <Switch
                checked={settings.weekly_summary}
                onCheckedChange={() => handleToggle('weekly_summary')}
              />
            </div>
          </div>

          <div className="border-t border-border pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Marketing Emails</p>
                <p className="text-sm text-muted-foreground">
                  Product updates and special offers
                </p>
              </div>
              <Switch
                checked={settings.marketing_emails}
                onCheckedChange={() => handleToggle('marketing_emails')}
              />
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
